import React from 'react';
import { useWindowDimensions } from 'react-native';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { Text } from '~/components/gluestack/text';
import { Heading } from '~/components/gluestack/heading';
import { Pressable } from '~/components/gluestack/pressable';
import { MemberCountLink } from '~/components/domain/common/MemberCountLink';
import { LocationWithFlagRow } from '~/components/ui/LocationWithFlagRow';
import { EventDetails } from '~/models/EventDetails';
import dayjs from '@/i18n/dayjs-config';
import { useTranslation } from 'react-i18next';
import { getCountryName } from '~/hooks/useCountryName';
import { useOpenMaps } from '~/hooks/useOpenMaps';

interface EventOverviewProps {
  event: EventDetails;
}

type DatePart = { text: string; bold?: boolean };

// \u200B (zero-width space) before the em dash allows line breaks to occur
// before the dash rather than mid-text, so the dash starts the new line if wrapping occurs.
const DASH = ' \u200B– ';

function formatEventDateRange(event: EventDetails): DatePart[] {
  const start = dayjs(event.StartDate);
  const end = event.EndDate ? dayjs(event.EndDate) : null;

  if (event.TimesShown) {
    if (end && !start.isSame(end, 'day')) {
      return [
        { text: start.format('ll'), bold: true },
        { text: ` ${start.format('LT')}` },
        { text: DASH },
        { text: end.format('ll'), bold: true },
        { text: ` ${end.format('LT')}` },
      ];
    }
    if (end) {
      return [
        { text: start.format('ll'), bold: true },
        { text: ` ${start.format('LT')}` },
        { text: DASH },
        { text: end.format('LT') },
      ];
    }
    return [
      { text: start.format('ll'), bold: true },
      { text: ` ${start.format('LT')}` },
    ];
  }

  if (end && !start.isSame(end, 'day')) {
    return [
      { text: start.format('ll'), bold: true },
      { text: DASH },
      { text: end.format('ll'), bold: true },
    ];
  }
  return [{ text: start.format('ll'), bold: true }];
}

export function EventOverview({ event }: EventOverviewProps) {
  const { t } = useTranslation();
  const openMaps = useOpenMaps();
  const { width } = useWindowDimensions();
  // Hide the section labels only while the large-screen profile header is in its
  // narrow single-column mode: from 768 up to the 1100 wide-header breakpoint
  // (matches ProfileScreen's isWideHeader), e.g. tablet portrait. Phones (<768)
  // use a different layout and keep the labels; the wide two-column header
  // (>=1100, e.g. iPad landscape) has room and keeps them too. Conditional
  // render rather than a className because NativeWind has no max-* breakpoints
  // and gluestack Text's web `display-inline` base defeats `hidden`/`flex`.
  const hideSectionLabels = width >= 768 && width < 1100;

  const dateParts = formatEventDateRange(event);
  const locationText = event.PlaceNameCleaned || getCountryName(event.CountryCode) || event.CountryCode || '';
  const mapsQuery = event.PlaceNameCleaned || event.Address || getCountryName(event.CountryCode) || event.CountryCode || '';
  const addressMapsQuery = event.Address
    ? event.PlaceNameCleaned
      ? `${event.Address} (${event.PlaceNameCleaned})`
      : event.Address
    : '';
  const hasLocation = !!(event.Address || event.PlaceNameCleaned || event.CountryCode);

  return (
    <VStack className="gap-4">
      <VStack className="gap-1">
        <Heading size="xl">{event.Name}</Heading>
        <HStack className="items-center gap-3">
          <MemberCountLink
            count={event.AttendeesCount}
            label={t('events.attendees', { count: event.AttendeesCount })}
            sheetTitle={t('events.attendees_short')}
            listType="following"
            memberId={event.Id}
          />
          <MemberCountLink
            count={event.FollowersCount}
            label={t('events.followers', { count: event.FollowersCount })}
            sheetTitle={t('events.followers_short')}
            listType="followers"
            memberId={event.Id}
          />
        </HStack>
      </VStack>

      <VStack className="gap-1">
        {!hideSectionLabels && (
          <Text size="xs" className="uppercase tracking-wide text-typography-600">
            {t('events.date_time')}
          </Text>
        )}
        <Text size="md" className="text-typography-700">
          {dateParts.map((part, i) =>
            part.bold ? (
              <Text key={i} size="md" className="font-semibold">
                {part.text}
              </Text>
            ) : (
              <Text key={i} size="md">
                {part.text}
              </Text>
            ),
          )}
        </Text>
        {event.TimeZoneIdentifier ? (
          <Text size="xs" className="text-typography-600">{event.TimeZoneIdentifier}</Text>
        ) : null}
      </VStack>

      {hasLocation && (
        <VStack className="gap-1">
          {!hideSectionLabels && (
            <Text size="xs" className="uppercase tracking-wide text-typography-600">
              {t('events.location')}
            </Text>
          )}
          <VStack className="gap-1">
            {event.Address ? (
              <Pressable
                onPress={() => addressMapsQuery && openMaps(addressMapsQuery)}
                hitSlop={8}>
                <Text size="md" className="font-medium text-typography-900 web:hover:underline">
                  {event.Address}
                </Text>
              </Pressable>
            ) : null}
            <LocationWithFlagRow
              mapsQuery={mapsQuery}
              countryCode={event.CountryCode}
              locationText={locationText}
              distanceKm={event.DistanceKm}
            />
          </VStack>
        </VStack>
      )}
    </VStack>
  );
}
